import { ChevronDown, ChevronUp } from '@styled-icons/feather';
import classNames from 'classnames';
import {
  cloneElement,
  createContext,
  Dispatch,
  isValidElement,
  PropsWithChildren,
  SetStateAction,
  useContext,
  useMemo,
  useRef,
  useState,
} from 'react';
import { animated, useTransition } from 'react-spring';
import { useOutsideAlerter, useOutsideEvent } from '../../utils/eventAlerter';
import styles from './Dropdown.module.css';

interface DropdownContextInterface {
  isDropdownOpen: boolean
  setIsDropdownOpen: Dispatch<SetStateAction<boolean>>
}

export const DropdownContext = createContext<DropdownContextInterface>({
  isDropdownOpen: false,
  setIsDropdownOpen: () => {
    throw Error(
      'You forgot to wrap this component around DropdownContext Provider',
    );
  },
});

export function DropdownItem(props: { children: React.ReactNode }) {
  const { children } = props;

  const { setIsDropdownOpen } = useContext(DropdownContext);

  if (!isValidElement(children)) return null;

  const { onClick: childOnClick, className: childClassName } = children.props;

  return cloneElement(children, {
    className: classNames(childClassName, styles.dropdownMenuItem),
    onClick: () => {
      if (childOnClick) childOnClick();
      setIsDropdownOpen(false);
    },
  });
}

type DropdownProps = PropsWithChildren<{
  disabled?: boolean
  triggerLabel: React.ReactNode
  dropDownMenuStyle?: React.CSSProperties
}>;

export function Dropdown(props: DropdownProps) {
  const {
    disabled, triggerLabel, children, dropDownMenuStyle,
  } = props;

  const [isDropdownOpen, setIsDropdownOpen] = useState(false);

  // When clicked outside of the menu and it's not selecting anything in the editor
  // close the menu
  const ref = useOutsideEvent<HTMLDivElement>('mousedown', (event) => {
    if (
      !(event.target as Element)?.classList?.contains?.('menu-dropdown-trigger')
    ) {
      setIsDropdownOpen(false);
    }
  });

  const dropdownRef = useRef<HTMLDivElement | null>(null);
  useOutsideAlerter(dropdownRef, '', () => setIsDropdownOpen(false));

  const ChevronIcon = isDropdownOpen ? ChevronUp : ChevronDown;

  const transitions = useTransition(isDropdownOpen, {
    from: { opacity: 0 },
    enter: { opacity: 1 },
    leave: { opacity: 0 },
  });

  return (
    <DropdownContext.Provider
      value={useMemo(
        () => ({
          isDropdownOpen,
          setIsDropdownOpen,
        }),
        [isDropdownOpen, setIsDropdownOpen],
      )}
    >
      <div ref={dropdownRef} className={classNames(styles.fullWidth, styles.relative)}>
        <button
          type="button"
          className={classNames(
            'menu-dropdown-trigger',
            styles.dropdownTrigger,
            styles.fullWidth,
            {
              [styles.dropdownTriggerDisabled]: disabled,
              [styles.dropdownTriggerOpen]: isDropdownOpen,
            },
          )}
          aria-haspopup="true"
          aria-controls="dropdown-menu"
          onClick={() => !disabled && setIsDropdownOpen((prevOpen) => !prevOpen)}
        >
          {!disabled ? (
            <div className="is-flex is-align-items-center">
              <div className="is-flex-grow-1">{triggerLabel || ''}</div>
              <ChevronIcon size={16} className="ml-1" />
            </div>
          ) : (
            triggerLabel
          )}
        </button>
        {transitions(
          (transitionStyle, isShow) => isShow && (
          <animated.div
            className={classNames('dropdown-menu', styles.dropdownMenu)}
            id="dropdown-menu"
            role="menu"
            ref={ref}
            style={transitionStyle}
          >
            <div
              className={classNames(styles.dropdownMenuContainer, {
                [styles.dropdownMenuContainerActive]: isDropdownOpen,
              })}
              style={dropDownMenuStyle}
            >
              {children}
            </div>
          </animated.div>
          ),
        )}
      </div>
    </DropdownContext.Provider>
  );
}

Dropdown.defaultProps = {
  disabled: false,
  dropDownMenuStyle: undefined,
};
